import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Chip,
  Button,
  Alert,
  LinearProgress,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Snackbar,
  Link,
  alpha,
} from '@mui/material';
import {
  LinkOff,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  Connection,
  ConnectionService,
  GetAllConnectionsParams,
} from '../../../services/connectionService';
import SysDateRangePicker from '../../../components/SysDateRangePicker';
import { formatarDataHoraSegundos, formatarDuracaoTotal } from '../../../utils/formatToView';

/* ------------------------------------------------------------------ helpers */
const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const toYmd = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { startDate: toYmd(first), endDate: toYmd(last) };
};

const parseYmdToLocalDate = (str: string): Date => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const dateToYmd = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* ------------------------------------------------------------------ props */
interface DesconexoesTabProps {
  embedded?: boolean;
}

/* ================================================================== component */
const DesconexoesTab: React.FC<DesconexoesTabProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = embedded ? theme.palette.error.main : theme.palette.primary.main;

  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConnections, setTotalConnections] = useState(0);

  /* ordenação */
  type SortableCol = 'acctstarttime' | 'acctstoptime' | 'framedipaddress';
  const [sortCol, setSortCol] = useState<SortableCol>('acctstoptime');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc'); // mais recente primeiro

  const handleSort = (col: SortableCol) => {
    if (sortCol === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  const sortedConnections = useMemo(() => {
    return [...connections].sort((a, b) => {
      const aVal = a[sortCol] ?? '';
      const bVal = b[sortCol] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [connections, sortCol, sortDir]);

  /* filtros */
  const currentMonth = getCurrentMonthRange();
  const [startDate, setStartDate] = useState(currentMonth.startDate);
  const [endDate, setEndDate] = useState(currentMonth.endDate);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  /* detalhes */
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  /* ---------------------------------------------------------------- load */
  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: GetAllConnectionsParams = {
        page: page + 1,
        limit: pageSize,
        onlyActive: false,   // somente encerradas
        onlyClosed: true,    // garante que acctstoptime está preenchido
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        username: usernameFilter.trim() || undefined,
        ip: ipFilter.trim() || undefined,
      };

      const response = await ConnectionService.getAll(params);

      if (response.success) {
        // segurança extra: descartar registros sem data de fim
        const closed = response.data.filter((c) => !!c.acctstoptime);
        setConnections(closed);
        setTotalPages(response.pagination.pages);
        setTotalConnections(response.pagination.total);
      } else {
        setError('Erro ao carregar desconexões');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(`Erro ao carregar desconexões: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, [page, pageSize]);

  /* ---------------------------------------------------------------- handlers */
  const handlePageChange = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
    setPage(0);
  };

  const handleViewDetails = (connection: Connection) => {
    setSelectedConnection(connection);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedConnection(null);
  };

  const applyFilters = () => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start > end) {
        setToastMessage('Data de início não pode ser maior que data fim');
        setToastOpen(true);
        return;
      }
    }
    setPage(1);
    loadConnections();
  };

  const clearFilters = () => {
    const range = getCurrentMonthRange();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setUsernameFilter('');
    setIpFilter('');
    setPage(1);
    setError(null);
    setTimeout(() => loadConnections(), 100);
  };

  /* ---------------------------------------------------------------- loading / error states */
  if (loading && connections.length === 0) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando desconexões...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  /* ---------------------------------------------------------------- shared sub-components */
  const FilterBar = () => (
    <Paper elevation={2} sx={{ mb: embedded ? 1 : 1, p: 0.9, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={embedded ? 1 : 2}>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Usuário"
            value={usernameFilter}
            onChange={(e) => setUsernameFilter(e.target.value)}
            placeholder="Filtrar por usuário"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="IP"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
            placeholder="Filtrar por IP"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SysDateRangePicker
            value={{
              startDate: startDate ? parseYmdToLocalDate(startDate) : null,
              endDate: endDate ? parseYmdToLocalDate(endDate) : null,
            }}
            onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
              setStartDate(dateToYmd(range.startDate));
              setEndDate(dateToYmd(range.endDate));
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box display="flex" gap={embedded ? 0.5 : 1}>
            <Button variant="contained" onClick={applyFilters} disabled={loading} sx={{ flex: 1 }}>
              Aplicar
            </Button>
            <Button variant="outlined" onClick={clearFilters} disabled={loading} sx={{ flex: 1 }}>
              Limpar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const SortHeader = ({ col, label }: { col: SortableCol; label: string }) => (
    <TableCell sortDirection={sortCol === col ? sortDir : false}>
      <TableSortLabel
        active={sortCol === col}
        direction={sortCol === col ? sortDir : 'desc'}
        onClick={() => handleSort(col)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const EmptyState = ({ compact }: { compact?: boolean }) => (
    <Box textAlign="center" py={compact ? 2 : 4} sx={compact ? { px: 1.5, pb: 1.5 } : undefined}>
      <LinkOff sx={{ fontSize: compact ? 48 : 64, color: 'text.secondary', mb: compact ? 1 : 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Nenhuma desconexão encontrada
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {startDate || endDate || usernameFilter || ipFilter
          ? 'Nenhuma desconexão encontrada com os filtros aplicados'
          : 'Não há registros de desconexão no período selecionado'}
      </Typography>
    </Box>
  );

  /* ---------------------------------------------------------------- render */
  return (
    <Box>
      <FilterBar />

      {/* ---- layout embedded (Paper compacto) ---- */}
      {embedded ? (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {/* cabeçalho da tabela */}
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                Desconexões ({totalConnections})
              </Typography>
              <Chip
                label="Encerradas"
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: '0.68rem', height: 20 }}
              />
            </Box>
            <Tooltip title="Atualizar">
              <IconButton onClick={loadConnections} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {sortedConnections.length === 0 ? (
            <EmptyState compact />
          ) : (
            <>
              <TableContainer>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        '& .MuiTableCell-head': {
                          bgcolor: (t) => `color-mix(in srgb, ${accentColor} 12%, ${t.palette.background.paper})`,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          py: 0.5,
                          borderBottom: `2px solid ${alpha(accentColor, 0.2)}`,
                        },
                      }}
                    >
                      <TableCell>Usuário</TableCell>
                      <SortHeader col="acctstarttime" label="Início" />
                      <SortHeader col="acctstoptime" label="Fim ↓" />
                      <TableCell>Duração</TableCell>
                      <SortHeader col="framedipaddress" label="IP" />
                      <TableCell>PPPoE Server</TableCell>
                      <TableCell>Interface</TableCell>
                      <TableCell>Download</TableCell>
                      <TableCell>Upload</TableCell>
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedConnections.map((connection) => (
                      <TableRow
                        key={connection.radacctid}
                        hover
                        sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}
                      >
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{connection.username || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctstarttime ? formatarDataHoraSegundos(connection.acctstarttime) : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctstoptime ? formatarDataHoraSegundos(connection.acctstoptime) : '—'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctsessiontime ? formatarDuracaoTotal(connection.acctsessiontime) : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          <Link href={`http://${connection.framedipaddress}`} target="_blank" rel="noopener noreferrer">
                            {connection.framedipaddress || 'N/A'}
                          </Link>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{connection.calledstationid || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>{connection.nasportid || 'N/A'}</TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctoutputoctets
                            ? ConnectionService.formatBytes(connection.acctoutputoctets)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                          {connection.acctinputoctets
                            ? ConnectionService.formatBytes(connection.acctinputoctets)
                            : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Tooltip title="Ver detalhes">
                            <IconButton size="small" onClick={() => handleViewDetails(connection)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[20, 50, 75, 100]}
                component="div"
                count={totalConnections}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePageSizeChange}
                labelRowsPerPage="Linhas por pág:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    minHeight: 32,
                    flexWrap: 'nowrap',
                    justifyContent: 'center',
                    gap: 1,
                    p: 0,
                    '& .MuiTablePagination-spacer': { display: 'none' },
                  },
                  '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                  '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                  '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                }}
              />
            </>
          )}
        </Paper>
      ) : (
        /* ---- layout standalone (Card) ---- */
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">Desconexões ({totalConnections})</Typography>
                <Chip label="Encerradas" size="small" color="error" variant="outlined" />
              </Box>
              <Tooltip title="Atualizar">
                <IconButton onClick={loadConnections} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            {sortedConnections.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size={isMobile ? 'small' : 'medium'}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuário</TableCell>
                        <SortHeader col="acctstarttime" label="Início" />
                        <SortHeader col="acctstoptime" label="Fim ↓" />
                        <TableCell>Duração</TableCell>
                        <SortHeader col="framedipaddress" label="IP" />
                        {!isMobile && <TableCell>PPPoE Server</TableCell>}
                        {!isMobile && <TableCell>Interface</TableCell>}
                        {!isMobile && <TableCell>Download</TableCell>}
                        {!isMobile && <TableCell>Upload</TableCell>}
                        <TableCell>Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedConnections.map((connection) => (
                        <TableRow
                          key={connection.radacctid}
                          hover
                          sx={{ '&:hover': { bgcolor: alpha(accentColor, 0.03) } }}
                        >
                          <TableCell>{connection.username || 'N/A'}</TableCell>
                          <TableCell>
                            {connection.acctstarttime ? formatarDataHoraSegundos(connection.acctstarttime) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {connection.acctstoptime ? formatarDataHoraSegundos(connection.acctstoptime) : '—'}
                          </TableCell>
                          <TableCell>
                            {connection.acctsessiontime ? formatarDuracaoTotal(connection.acctsessiontime) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Link href={`http://${connection.framedipaddress}`} target="_blank" rel="noopener noreferrer">
                              {connection.framedipaddress || 'N/A'}
                            </Link>
                          </TableCell>
                          {!isMobile && <TableCell>{connection.calledstationid || 'N/A'}</TableCell>}
                          {!isMobile && <TableCell>{connection.nasportid || 'N/A'}</TableCell>}
                          {!isMobile && (
                            <TableCell>
                              {connection.acctoutputoctets
                                ? ConnectionService.formatBytes(connection.acctoutputoctets)
                                : 'N/A'}
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {connection.acctinputoctets
                                ? ConnectionService.formatBytes(connection.acctinputoctets)
                                : 'N/A'}
                            </TableCell>
                          )}
                          <TableCell>
                            <Tooltip title="Ver detalhes">
                              <IconButton size="small" onClick={() => handleViewDetails(connection)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[20, 50, 75, 100]}
                  component="div"
                  count={totalConnections}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handlePageSizeChange}
                  labelRowsPerPage="Linhas por pág:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  sx={{
                    mt: 2,
                    '.MuiTablePagination-toolbar': {
                      minHeight: 32,
                      flexWrap: 'nowrap',
                      justifyContent: 'center',
                      gap: 1,
                      p: 0,
                      '& .MuiTablePagination-spacer': { display: 'none' },
                    },
                    '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                    '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- Dialog de detalhes ---- */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Detalhes da Desconexão</DialogTitle>
        <DialogContent>
          {selectedConnection && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">ID da Sessão</Typography>
                <Typography variant="body1" gutterBottom>{selectedConnection.acctsessionid}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Usuário</Typography>
                <Typography variant="body1" gutterBottom>{selectedConnection.username || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">IP do Cliente</Typography>
                <Typography variant="body1" gutterBottom>{selectedConnection.framedipaddress || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Início</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstarttime
                    ? formatarDataHoraSegundos(selectedConnection.acctstarttime)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Fim</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstoptime
                    ? formatarDataHoraSegundos(selectedConnection.acctstoptime)
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Duração Total</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctsessiontime
                    ? formatarDuracaoTotal(selectedConnection.acctsessiontime)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">PPPoE Server</Typography>
                <Typography variant="body1" gutterBottom>{selectedConnection.calledstationid || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Interface</Typography>
                <Typography variant="body1" gutterBottom>{selectedConnection.nasportid || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Download</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctoutputoctets
                    ? ConnectionService.formatBytes(selectedConnection.acctoutputoctets)
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Upload</Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctinputoctets
                    ? ConnectionService.formatBytes(selectedConnection.acctinputoctets)
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Toast ---- */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DesconexoesTab;
